import { Box, Card, Heading, Text } from "@chakra-ui/react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { TeacherQuizAnalyticsRow } from "@/types/dashboard"

interface QuizActivityChartsProps {
  rows: TeacherQuizAnalyticsRow[]
}

const QUIZ_AXIS_LABEL_LENGTH = 22

export function formatQuizChartLabel(name: string): string {
  return name.length <= QUIZ_AXIS_LABEL_LENGTH
    ? name
    : `${name.slice(0, QUIZ_AXIS_LABEL_LENGTH - 1)}…`
}

export function getTopQuizRows(
  rows: TeacherQuizAnalyticsRow[],
): TeacherQuizAnalyticsRow[] {
  return [...rows]
    .sort((left, right) => right.started_attempts - left.started_attempts)
    .slice(0, 10)
}

export function QuizActivityCharts({ rows }: QuizActivityChartsProps) {
  const topQuizzes = getTopQuizRows(rows)
  const scoreQuizzes = topQuizzes.filter(
    (quiz) => quiz.avg_score_percentage !== null,
  )

  if (rows.length === 0) {
    return (
      <Text color="fg.muted" data-testid="quiz-activity-empty-state">
        No quiz activity in the selected scope.
      </Text>
    )
  }

  return (
    <Box data-testid="quiz-activity-charts">
      <Card.Root
        data-testid="quiz-start-completion-chart"
        data-quiz-ids={topQuizzes.map((quiz) => quiz.quiz_id).join(",")}
      >
        <Card.Header>
          <Heading size="md">Top quizzes by activity</Heading>
          <Text color="fg.muted" fontSize="sm">
            Started versus completed attempts
          </Text>
        </Card.Header>
        <Card.Body>
          <Box h={{ base: "360px", md: "420px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topQuizzes}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="quiz_name"
                  tickFormatter={formatQuizChartLabel}
                  width={170}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="started_attempts" fill="#4A5568" name="Started" />
                <Bar
                  dataKey="completed_attempts"
                  fill="#3182CE"
                  name="Completed"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Card.Body>
      </Card.Root>

      <Card.Root
        mt={6}
        data-testid="quiz-score-chart"
        data-quiz-ids={scoreQuizzes.map((quiz) => quiz.quiz_id).join(",")}
      >
        <Card.Header>
          <Heading size="md">Top quiz scores</Heading>
          <Text color="fg.muted" fontSize="sm">
            Student-weighted average score percentage
          </Text>
        </Card.Header>
        <Card.Body>
          {scoreQuizzes.length === 0 ? (
            <Text color="fg.muted">
              No completed quiz scores in this scope.
            </Text>
          ) : (
            <Box h={{ base: "360px", md: "420px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={scoreQuizzes}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis
                    type="category"
                    dataKey="quiz_name"
                    tickFormatter={formatQuizChartLabel}
                    width={170}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="avg_score_percentage"
                    fill="#38A169"
                    name="Avg score %"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Card.Body>
      </Card.Root>
    </Box>
  )
}
